import { Link } from 'react-router-dom'

const guide = {
  slug: 'cold-turkey-for-iphone',
  category: 'compare',
  kicker: 'Compare · Cold Turkey',
  title: 'Cold Turkey for iPhone? Why there isn\'t one, and what actually locks an iPhone',
  seoTitle: 'Cold Turkey for iPhone: The Closest Thing That Locks',
  description: `Cold Turkey is a desktop blocker for Windows and Mac; there is no iPhone version. Why iOS makes that hard, and the one mechanism that actually locks an iPhone.`,
  published: '2026-09-03',
  minutes: 6,
  intro: `Ask a forum for a blocker that can't be switched off and someone
          will name Cold Turkey. It's a desktop app, for Windows and macOS,
          with a reputation for being hard to get around once a block is
          running. Then the follow-up question comes, every time: is there a
          Cold Turkey for iPhone? There isn't, and the reason says a lot about
          what does and doesn't hold on iOS.`,
  next: ['porn-blocker-you-cant-uninstall', 'supervised-iphone-explained', 'how-to-block-porn-on-iphone'],
  body: (
    <>
      <h2>What Cold Turkey is</h2>
      <p>
        Cold Turkey, per its own site, is a website and application blocker
        for Windows and Mac. You schedule or start a block, and during the
        block the app makes itself hard to stop: the design goal is that
        once you've committed, you can't talk yourself out of it. It's the
        blocker people recommend for a work computer, and it earns the
        recommendation.
      </p>
      <p>
        It has no iPhone or iPad version. That's not an oversight.
      </p>

      <h2>Why a Cold Turkey for iPhone can't exist as an app</h2>
      <p>
        On a desktop, an app can run as a background service, watch other
        programs, edit system files and refuse to quit. iOS allows none of
        that to a third-party app. An iPhone app can't watch what other apps
        do, can't stop you deleting it from the home screen, and can't
        prevent you installing another browser. The tools Apple does give
        app makers are:
      </p>
      <ul>
        <li><b>Screen Time APIs</b>, which let an app schedule blocks through Apple's own restrictions, protected by a Screen Time passcode you hold (and can reset with your Apple ID).</li>
        <li><b>A DNS or VPN-style filter</b>, which covers every app but is removed like any profile or app.</li>
      </ul>
      <p>
        So every "Cold Turkey for iPhone" candidate ends up being one of two
        things: a Screen Time front end, or a filter you can delete. Both are
        undone in under a minute by the person who set them up. The full
        list of levels: <Link to="/guides/porn-blocker-you-cant-uninstall">what makes a blocker immovable</Link>.
      </p>

      <h2>The one mechanism on iOS that does hold</h2>
      <p>
        The exception isn't an app. It's a mode of the device.
        <b> Supervision</b> is what Apple built for organisations that hand
        out iPhones; a supervised iPhone can carry a configuration profile
        that <em>cannot be removed from the device</em>, that sets a filtering
        DNS resolver for every app, and that forbids adding a VPN. Nothing on
        the phone can undo it, and there's no passcode to recover because
        there is none. That's the iPhone equivalent of "you committed, you
        can't talk yourself out of it".
        <Link to="/guides/supervised-iphone-explained"> Supervised iPhone explained</Link>.
      </p>
      <p>
        That is how SELFCNTRL works on iPhone and iPad. Setting it up needs
        a Mac or a Windows PC once, for about ten minutes, and can involve a
        backup and restore; after that the phone is a phone, with one door
        shut. Loosening goes through your account and waits twenty-four
        hours; tightening is immediate; the floor never drops.
      </p>

      <h2>Where the two overlap: your computer</h2>
      <p>
        Cold Turkey and SELFCNTRL both run on Windows and Mac, and they do
        different things. Cold Turkey blocks sites and apps you list, on a
        schedule, on that machine. SELFCNTRL filters pornography by category
        at DNS level on that machine, enforces safe search, adds night
        curfews, and ties every loosening to a twenty-four-hour delay in an
        account shared with your phone. If your problem is focus, Cold Turkey
        is the better tool. If your problem is porn, the phone is where 2am
        happens, and a desktop blocker doesn't reach it.
      </p>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>Cold Turkey</th><th>Screen Time apps on iPhone</th><th>SELFCNTRL</th></tr>
        </thead>
        <tbody>
          <tr><td>Runs on</td><td>Windows, Mac</td><td>iPhone, iPad</td><td>iPhone, iPad, Mac, Windows</td></tr>
          <tr><td>Blocks by</td><td>Your list of sites and apps</td><td>Apple's restrictions</td><td>Category at DNS level + your list</td></tr>
          <tr><td>Undone on iPhone by</td><td>—</td><td>Passcode / Apple ID reset</td><td>Only a request from the account, after 24 h</td></tr>
          <tr><td>VPN workaround on iPhone</td><td>—</td><td>Open</td><td>Closed (supervision)</td></tr>
          <tr><td>Built for</td><td>Focus, work</td><td>Screen time</td><td>Quitting porn</td></tr>
        </tbody>
      </table>
      <p>
        We describe Cold Turkey only by what its own site says it is: a
        desktop blocker for Windows and macOS. Check their site for current
        features and pricing.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Is there a Cold Turkey app for iPhone or iPad?',
      a: `No. Cold Turkey is a desktop blocker for Windows and macOS. iOS
          doesn't let a third-party app do what Cold Turkey does on a
          computer, so any iPhone "equivalent" is either a Screen Time
          front end or a removable filter.`
    },
    {
      q: 'What\'s the closest thing to Cold Turkey on iPhone?',
      a: `For focus, the Screen Time-based apps. For a block you genuinely
          can't undo on impulse, a supervised profile: it can't be removed
          from the device and forbids VPNs. That's how SELFCNTRL locks
          iPhone and iPad.`
    },
    {
      q: 'Can I use Cold Turkey and SELFCNTRL together?',
      a: `Yes, they don't conflict. Cold Turkey handles apps and focus
          schedules on your computer; SELFCNTRL filters adult content by
          category on every device, including the phone.`
    }
  ]
}

export default guide
